import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FULL_WIDTH_ROUTES } from '../constants/layout';
import { getBookingAttentionCount, getInviteCount } from '../lib/api';
import { Music, Wrench, FileText, User, LogOut, Menu, Info, Briefcase, CalendarDays, Bookmark, Bell, PlusCircle, ClipboardList, Home, Sparkles, ArrowLeftRight, UserPlus, Loader2, Play, Tag, Shield, Guitar, ListChecks, Speaker } from 'lucide-react';
import { Button } from './ui/button';
import Wordmark from './Wordmark';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { seedDemo } from '../lib/api';

export default function Layout() {
  const { user, logout, switchRole, addRole, hasRole, isDualRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [attentionCount, setAttentionCount] = useState(0);
  const [inviteCount, setInviteCount] = useState(0);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(false);

  const isDemo = user?.id === 'demo-venue-paradiso';

  const handleLoadDemo = async () => {
    setLoadingDemo(true);
    try {
      const res = await seedDemo();
      // Note: do NOT call checkAuth() here. Demo seeding is anonymous —
      // signing visitors in as the demo venue would leak authenticated chrome
      // (avatar / Logout / NEW REQUEST) onto the public Showtime demo.
      navigate(`/bookings/${res.data.booking_id}`);
    } catch { toast.error('Failed to load demo'); }
    finally { setLoadingDemo(false); }
  };

  useEffect(() => {
    if (user && (hasRole('MUSICIAN') || hasRole('TECH') || hasRole('VENUE'))) {
      loadAttentionCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  const loadAttentionCount = async () => {
    try {
      const [attentionRes] = await Promise.all([getBookingAttentionCount()]);
      setAttentionCount(attentionRes.data.count);
      if (hasRole('MUSICIAN') || hasRole('TECH')) {
        const inviteRes = await getInviteCount();
        setInviteCount(inviteRes.data.count || 0);
      }
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSwitchRole = async (targetType) => {
    if (switchingRole) return;
    setSwitchingRole(true);
    try {
      await switchRole(targetType);
      toast.success(`Switched to ${targetType === 'MUSICIAN' ? 'Musician' : 'Tech Crew'} profile`);
      navigate('/');
    } catch { toast.error('Failed to switch role'); }
    finally { setSwitchingRole(false); }
  };

  const handleAddRole = async (newType) => {
    if (switchingRole) return;
    setSwitchingRole(true);
    try {
      await addRole(newType);
      toast.success(`${newType === 'MUSICIAN' ? 'Musician' : 'Tech Crew'} profile created! Complete your onboarding.`);
      navigate(newType === 'MUSICIAN' ? '/onboarding/musician' : '/onboarding/tech');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add role'); }
    finally { setSwitchingRole(false); }
  };

  const navItems = [
    { path: '/requests', label: 'Requests', icon: FileText },
    { path: '/musicians', label: 'Musicians', icon: Music },
    { path: '/tech', label: 'Tech Crew', icon: Wrench },
  ];

  const getMyProfilePath = () => {
    if (!user) return null;
    switch (user.profileType) {
      case 'MUSICIAN': return '/musicians/me';
      case 'TECH': return '/tech/me';
      case 'VENUE': return '/venues/me';
      default: return null;
    }
  };

  const getOtherRoleType = () => {
    if (!user) return null;
    if (user.profileType === 'MUSICIAN') return 'TECH';
    if (user.profileType === 'TECH') return 'MUSICIAN';
    return null;
  };

  const canAddRole = () => {
    if (!user || user.profileType === 'VENUE') return false;
    return (user.profileTypes || [user.profileType]).length < 2;
  };

  const getAddableRole = () => !user ? null : hasRole('MUSICIAN') ? 'TECH' : 'MUSICIAN';

  const isNavActive = (item) =>
    (item.path === '/requests' && (location.pathname === '/' || location.pathname.startsWith('/requests')))
    || (item.path !== '/requests' && location.pathname.startsWith(item.path));

  // A request detail page is a leaf under /requests, not a browse index: no browse nav on it,
  // at any width (the phone's tab bar, the tablet and desktop header links). The other detail
  // routes (/musicians/:id, /tech/:id) still show it — unchanged on purpose.
  const isRequestDetail = /^\/requests\/[^/]+$/.test(location.pathname);

  /* ── Role badge (small pill) ── */
  const RoleBadge = ({ compact = false }) => {
    if (!user) return null;
    const type = user.profileType;
    const Icon = type === 'MUSICIAN' ? Music : type === 'TECH' ? Wrench : null;
    if (!Icon) return null;
    return (
      <span
        className={cn('inline-flex items-center gap-1 rounded', compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs')}
        style={{ background: 'rgba(200,146,62,0.12)', color: 'var(--amber)', fontFamily: 'var(--font-ui)', letterSpacing: '0.5px' }}
        data-testid="active-role-badge"
      >
        <Icon className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        {type === 'MUSICIAN' ? 'Musician' : 'Tech'}
      </span>
    );
  };

  /* ── Rider Check entry points, by role. Sits above Home in every menu.
     A dual-role account sees both sets; everyone else sees nothing here. ── */
  const riderMenuItems = (testIdPrefix = '') => {
    const musician = hasRole('MUSICIAN');
    const venue = hasRole('VENUE');
    if (!user || (!musician && !venue)) return null;
    return (
      <>
        {musician && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/musicians/me/rider" className="flex items-center text-[var(--t1)]" data-testid={`${testIdPrefix}nav-my-rider-link`}>
                <Guitar className="h-4 w-4 mr-2" />My rider
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/rider-check" className="flex items-center text-[var(--t1)]" data-testid={`${testIdPrefix}nav-rider-check-link`}>
                <ListChecks className="h-4 w-4 mr-2" />Rider Check
              </Link>
            </DropdownMenuItem>
          </>
        )}
        {venue && (
          <DropdownMenuItem asChild>
            <Link to="/venues/me/inventory" className="flex items-center text-[var(--t1)]" data-testid={`${testIdPrefix}nav-backline-link`}>
              <Speaker className="h-4 w-4 mr-2" />Equipment
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-[var(--b1)]" />
      </>
    );
  };

  /* ── Dropdown menu items (shared desktop & mobile) ── */
  const secondaryMenuItems = () => (
    <>
      {riderMenuItems()}
      <DropdownMenuItem asChild>
        <Link to="/landing" className="flex items-center text-[var(--t1)]" data-testid="home-link">
          <Home className="h-4 w-4 mr-2" />Home
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/how-it-works" className="flex items-center text-[var(--t1)]">
          <Info className="h-4 w-4 mr-2" />How it works
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/pricing" className="flex items-center text-[var(--t1)]" data-testid="pricing-link">
          <Tag className="h-4 w-4 mr-2" />Pricing
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/whats-new" className="flex items-center text-[var(--t1)]">
          <Sparkles className="h-4 w-4 mr-2" />What's New
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/privacy" className="flex items-center text-[var(--t1)]" data-testid="privacy-link">
          <Shield className="h-4 w-4 mr-2" />Privacy
        </Link>
      </DropdownMenuItem>
      {user && (
        <>
          <DropdownMenuSeparator className="bg-[var(--b1)]" />
          {/* Dual-role switcher inside menu */}
          {isDualRole() && (
            <DropdownMenuItem
              onClick={() => handleSwitchRole(getOtherRoleType())}
              disabled={switchingRole}
              data-testid="role-switcher-btn"
              className="text-[var(--amber)]"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Switch to {getOtherRoleType() === 'MUSICIAN' ? 'Musician' : 'Tech'}
            </DropdownMenuItem>
          )}
          {canAddRole() && (
            <DropdownMenuItem
              onClick={() => handleAddRole(getAddableRole())}
              data-testid="add-role-btn"
              className="text-[var(--amber)]"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add {getAddableRole() === 'MUSICIAN' ? 'Musician' : 'Tech Crew'} Profile
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator className="bg-[var(--b1)]" />
          <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-[var(--t2)]">
            <LogOut className="h-4 w-4 mr-2" />Logout
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col relative stage">
      {/* ════════ DESKTOP HEADER ════════ */}
      <header className="hidden lg:flex nav-bar" style={{ height: 56 }}>
        <div className="max-w-[1400px] mx-auto px-8 w-full h-full flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/landing" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <Wordmark className="h-[30px]" />
          </Link>

          {/* Center: Browse nav tabs */}
          <nav className="flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {isDemo && (
              <span
                data-testid="demo-mode-indicator"
                className="mr-2 px-2 py-0.5 rounded-full"
                style={{
                  fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
                  fontSize: 10,
                  letterSpacing: 0.5,
                  color: '#C8923E',
                  border: '1px solid rgba(200,146,62,0.4)',
                  background: 'transparent',
                  lineHeight: '16px',
                }}
              >
                DEMO MODE
              </span>
            )}
            {!isRequestDetail && navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn('nav-item', isNavActive(item) && 'nav-item-active')}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right: User actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleLoadDemo}
              disabled={loadingDemo}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium tracking-wide uppercase transition-all hover:bg-[rgba(200,146,62,0.08)]"
              style={{ border: '1px solid #C8923E', color: '#C8923E', fontFamily: "'Barlow', sans-serif", letterSpacing: '0.5px', background: 'transparent' }}
              data-testid="load-demo-btn"
            >
              {loadingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Showtime
            </button>
            {user && (
              <>
                {/* Role badge */}
                {isDualRole() && <RoleBadge />}

                {/* Quick actions based on role */}
                {user.profileType === 'VENUE' && (
                  <Link
                    to="/venues/requests/new"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium tracking-wide uppercase transition-all"
                    style={{ background: 'rgba(200,146,62,0.12)', color: 'var(--amber)', fontFamily: 'var(--font-ui)', letterSpacing: '1px' }}
                    data-testid="desktop-create-request"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />New Request
                  </Link>
                )}

                {(hasRole('MUSICIAN') || hasRole('TECH')) && (
                  <>
                    {/* Saved requests */}
                    <Link to="/saved" className="p-2 rounded-lg transition-colors hover:bg-[var(--s2)]" title="Saved" data-testid="desktop-saved-link">
                      <Bookmark className="h-4 w-4 text-[var(--t2)]" />
                    </Link>
                    {/* Invites */}
                    <Link to="/invites/pending" className="relative p-2 rounded-lg transition-colors hover:bg-[var(--s2)]" title="Pending confirmations" data-testid="desktop-invites-link">
                      <Bell className="h-4 w-4 text-[var(--t2)]" />
                      {inviteCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-[var(--amber)] text-black text-[10px] font-bold rounded-full flex items-center justify-center">{inviteCount}</span>
                      )}
                    </Link>
                    {/* My Gigs */}
                    <Link to="/my-gigs" className="relative p-2 rounded-lg transition-colors hover:bg-[var(--s2)]" title="My Gigs" data-testid="desktop-gigs-link">
                      <Briefcase className="h-4 w-4 text-[var(--t2)]" />
                      {attentionCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-[var(--amber)] text-black text-[10px] font-bold rounded-full flex items-center justify-center">{attentionCount}</span>
                      )}
                    </Link>
                  </>
                )}

                {user.profileType === 'VENUE' && (
                  <Link to="/venues/requests" className="relative p-2 rounded-lg transition-colors hover:bg-[var(--s2)]" title="Manage Requests" data-testid="desktop-manage-link">
                    <ClipboardList className="h-4 w-4 text-[var(--t2)]" />
                    {attentionCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-[var(--amber)] text-black text-[10px] font-bold rounded-full flex items-center justify-center">{attentionCount}</span>
                    )}
                  </Link>
                )}

                {/* Profile link */}
                {getMyProfilePath() && (
                  <Link
                    to={getMyProfilePath()}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors hover:bg-[var(--s2)]"
                    data-testid="my-profile-link"
                  >
                    <div className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: 'var(--s2)', color: 'var(--amber)', border: '1px solid var(--b2)' }}>
                      {user.email?.charAt(0).toUpperCase()}
                    </div>
                  </Link>
                )}
              </>
            )}
            {!user && (
              <>
                <Link to="/login" className="nav-item text-sm">Sign In</Link>
                <Link
                  to="/signup"
                  className="flex items-center px-4 py-2 rounded-lg text-xs font-medium tracking-wide uppercase"
                  style={{ background: 'linear-gradient(180deg, #D4A044 0%, #C8923E 100%)', color: '#0E1110', fontFamily: 'var(--font-ui)', letterSpacing: '1.5px' }}
                  data-testid="desktop-signup-btn"
                >
                  Get Started
                </Link>
              </>
            )}

            {/* More menu — always available, logged in or not */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-[var(--t2)] hover:text-[var(--t0)] hover:bg-[var(--s1)] h-8 w-8" data-testid="menu-btn">
                  <Menu className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-[var(--s1)] border-[var(--b2)]">
                {secondaryMenuItems()}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Below 1024px a gig opens as its own full screen (the drill). Its bar is the brand and
          the menu only: the wordmark left, the menu right. No section tabs or Showtime, since
          the gig is the whole screen and the docked bar at the foot holds its actions. */}
      {isRequestDetail ? (
        <header className="lg:hidden nav-bar flex" data-testid="drill-bar">
          {/* Lined up with the page below it: the wordmark starts on the title's left edge and
              the menu glyph ends on the cards' right edge. The 44px button reaches 14px past
              that edge (its 12px inset plus the glyph's own 2.5px), so it is the glyph, not
              the button, that aligns. */}
          <div className="w-full h-full flex items-center justify-between pl-1 md:pl-[26px] pr-1 md:pr-[26px]">
            <Link to="/landing" className="flex-shrink-0" data-testid="mobile-logo-link">
              <Wordmark className="h-[26px] md:h-[30px]" />
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-[var(--t0)] flex-shrink-0 h-11 w-11 -mr-[14px]" aria-label="Menu" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[var(--s1)] border-[var(--b2)]">
                {mobileMenuItems()}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
      ) : (
        <>
        {/* ════════ TABLET HEADER (md only) ════════ */}
        <header className="hidden md:flex lg:hidden nav-bar">
          <div className="max-w-7xl mx-auto px-6 w-full h-full flex items-center justify-between">
            <Link to="/landing" className="flex items-center gap-2" data-testid="tablet-logo-link">
              <Wordmark className="h-[30px]" />
            </Link>
            <nav className="flex items-center gap-1">
              {isDemo && (
                <span
                  data-testid="tablet-demo-mode-indicator"
                  className="mr-1.5 px-1.5 py-0.5 rounded-full"
                  style={{ fontFamily: "'Barlow Condensed', 'Barlow', sans-serif", fontSize: 9, letterSpacing: 0.5, color: '#C8923E', border: '1px solid rgba(200,146,62,0.4)', background: 'transparent', lineHeight: '14px' }}
                >
                  DEMO
                </span>
              )}
              {!isRequestDetail && navItems.map((item) => (
                <Link key={item.path} to={item.path} className={cn('nav-item', isNavActive(item) && 'nav-item-active')} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <button
                onClick={handleLoadDemo}
                disabled={loadingDemo}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs tracking-wide uppercase transition-all hover:bg-[rgba(200,146,62,0.08)]"
                style={{ border: '1px solid #C8923E', color: '#C8923E', fontFamily: "'Barlow', sans-serif", background: 'transparent' }}
                data-testid="tablet-load-demo-btn"
              >
                {loadingDemo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                Showtime
              </button>
              {user && isDualRole() && <RoleBadge compact />}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-[var(--t2)] hover:text-[var(--t0)] hover:bg-[var(--s1)]" data-testid="tablet-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[var(--s1)] border-[var(--b2)]">
                  {mobileMenuItems()}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* ════════ MOBILE HEADER ════════ */}
        <header className="md:hidden nav-bar flex">
          <div className="w-full h-full flex items-center justify-between px-3">
            <Link to="/landing" className="flex-shrink-0" data-testid="mobile-logo-link">
              <Wordmark className="h-[26px]" />
            </Link>
            <div className="flex items-center gap-1.5">
              {isDemo && (
                <span
                  data-testid="mobile-demo-mode-indicator"
                  className="px-1.5 py-0.5 rounded-full"
                  style={{ fontFamily: "'Barlow Condensed', 'Barlow', sans-serif", fontSize: 9, letterSpacing: 0.5, color: '#C8923E', border: '1px solid rgba(200,146,62,0.4)', background: 'transparent', lineHeight: '14px' }}
                >
                  DEMO
                </span>
              )}
              <button
                onClick={handleLoadDemo}
                disabled={loadingDemo}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[13px] tracking-wide uppercase transition-all hover:bg-[rgba(200,146,62,0.08)]"
                style={{ border: '1px solid #C8923E', color: '#C8923E', fontFamily: "'Barlow', sans-serif", background: 'transparent' }}
                data-testid="mobile-load-demo-btn"
              >
                {loadingDemo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                Showtime
              </button>
              {user && isDualRole() && <RoleBadge compact />}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-[var(--t0)] flex-shrink-0 h-9 w-9" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[var(--s1)] border-[var(--b2)]">
                  {mobileMenuItems()}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        </>
      )}

      {/* ════════ MOBILE BROWSE TAB BAR ════════ */}
      {(() => {
        // Without the isRequestDetail test the startsWith below would hand a gig page a tab bar
        // with nothing to browse.
        const isBrowsePage = !isRequestDetail && ['/', '/requests', '/musicians', '/tech'].some(p =>
          p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
        );
        if (!isBrowsePage) return null;
        const activeIndex = navItems.findIndex(item => isNavActive(item));
        return (
          <div className="md:hidden" style={{ background: 'var(--bg-page)', borderBottom: '1px solid var(--border-subtle)' }} data-testid="browse-tab-bar">
            <div className="max-w-7xl mx-auto">
              <div className="relative flex">
                <div
                  className="absolute bottom-0 h-0.5 transition-transform duration-250 ease-out"
                  style={{ width: `${100 / navItems.length}%`, transform: `translateX(${Math.max(activeIndex, 0) * 100}%)`, background: 'var(--s8-amber)' }}
                />
                {navItems.map((item) => (
                  <Link
                    key={item.path} to={item.path}
                    className={cn('flex-1 text-center py-3 relative z-10 transition-colors duration-200', isNavActive(item) ? 'text-[var(--s8-amber)]' : 'text-[var(--text-muted)]')}
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '15.5px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase' }}
                    data-testid={`browse-tab-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ════════ MAIN CONTENT ════════ */}
      <main className="flex-1 w-full min-w-0 relative z-10">
        {FULL_WIDTH_ROUTES.some(r => r.test(location.pathname)) ? (
          <Outlet />
        ) : (
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-8 pb-10">
            <Outlet />
          </div>
        )}
      </main>
    </div>
  );

  /* ── Full mobile/tablet menu ── */
  function mobileMenuItems() {
    return (
      <>
        {riderMenuItems('mobile-')}
        <DropdownMenuItem asChild>
          <Link to="/landing" className="flex items-center text-[var(--t1)]" data-testid="mobile-home-link"><Home className="h-4 w-4 mr-2" />Home</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/how-it-works" className="flex items-center text-[var(--t1)]"><Info className="h-4 w-4 mr-2" />How it works</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/whats-new" className="flex items-center text-[var(--t1)]" data-testid="mobile-whats-new-link"><Sparkles className="h-4 w-4 mr-2" />What's New</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/privacy" className="flex items-center text-[var(--t1)]" data-testid="mobile-privacy-link"><Shield className="h-4 w-4 mr-2" />Privacy</Link>
        </DropdownMenuItem>
        {user && (
          <>
            <DropdownMenuSeparator className="bg-[var(--b1)]" />
            {isDualRole() && (
              <DropdownMenuItem onClick={() => handleSwitchRole(getOtherRoleType())} disabled={switchingRole} data-testid="role-switcher-btn" className="text-[var(--amber)]">
                <ArrowLeftRight className="h-4 w-4 mr-2" />Switch to {getOtherRoleType() === 'MUSICIAN' ? 'Musician' : 'Tech'}
              </DropdownMenuItem>
            )}
            {canAddRole() && (
              <DropdownMenuItem onClick={() => handleAddRole(getAddableRole())} data-testid="add-role-btn" className="text-[var(--amber)]">
                <UserPlus className="h-4 w-4 mr-2" />Add {getAddableRole() === 'MUSICIAN' ? 'Musician' : 'Tech Crew'} Profile
              </DropdownMenuItem>
            )}
            {getMyProfilePath() && (
              <DropdownMenuItem asChild>
                <Link to={getMyProfilePath()} data-testid="my-profile-link-mobile" className="text-[var(--t1)]"><User className="h-4 w-4 mr-2" />My Profile</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to="/saved" data-testid="saved-requests-link" className="text-[var(--t1)]"><Bookmark className="h-4 w-4 mr-2" />Saved Requests</Link>
            </DropdownMenuItem>
            {user.profileType === 'VENUE' && (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/venues/requests/new" data-testid="create-request-link" className="text-[var(--t1)]"><PlusCircle className="h-4 w-4 mr-2" />Create Request</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/venues/requests" data-testid="manage-requests-link" className="relative text-[var(--t1)]">
                    <ClipboardList className="h-4 w-4 mr-2" />Manage Requests
                    {attentionCount > 0 && <span className="ml-2 h-5 w-5 bg-[var(--amber)] text-black text-xs font-bold rounded-full inline-flex items-center justify-center">{attentionCount}</span>}
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            {(hasRole('MUSICIAN') || hasRole('TECH')) && (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/invites/pending" data-testid="pending-invites-link" className="relative flex items-center text-[var(--t1)]">
                    <Bell className="h-4 w-4 mr-2" />Pending Confirmations
                    {inviteCount > 0 && <span className="ml-2 h-5 w-5 bg-[var(--amber)] text-black text-xs font-bold rounded-full inline-flex items-center justify-center">{inviteCount}</span>}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/my-gigs" data-testid="my-gigs-link" className="relative flex items-center text-[var(--t1)]">
                    <Briefcase className="h-4 w-4 mr-2" />My Gigs
                    {attentionCount > 0 && <span className="ml-2 h-5 w-5 bg-[var(--amber)] text-black text-xs font-bold rounded-full inline-flex items-center justify-center">{attentionCount}</span>}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={user.profileType === 'MUSICIAN' ? '/musicians/me/availability' : '/tech/me/availability'} data-testid="availability-link" className="text-[var(--t1)]">
                    <CalendarDays className="h-4 w-4 mr-2" />My Availability
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator className="bg-[var(--b1)]" />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-[var(--t1)]"><LogOut className="h-4 w-4 mr-2" />Logout</DropdownMenuItem>
          </>
        )}
        {!user && (
          <>
            <DropdownMenuSeparator className="bg-[var(--b1)]" />
            <DropdownMenuItem asChild><Link to="/login" className="text-[var(--t1)]">Login</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/signup" className="text-[var(--t1)]">Sign up</Link></DropdownMenuItem>
          </>
        )}
      </>
    );
  }
}
